import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, openPdf } from "../lib/api";
import { fmtMoney, fmtDateTime, Badge, PAYMENT_METHODS } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, selectCls, btnPrimary, btnSecondary, btnDanger } from "../components/Modal";
import { toast } from "sonner";
import { Printer, Ban, Coins, RotateCcw } from "lucide-react";

export default function Sales() {
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const cur = user?.currency || "FCFA";
  const [rows, setRows] = useState([]);
  const [pg, setPg] = useState({ total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [settleFor, setSettleFor] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [settle, setSettle] = useState({ method: "Espèces", amount: "" });
  const [statusFilter, setStatusFilter] = useState("");

  const load = (p = page, s = search) =>
    api.get("/sales", { params: { status: statusFilter, search: s, page: p, limit: 25 } })
      .then((r) => { setRows(r.data.items); setPg({ total: r.data.total, pages: r.data.pages }); })
      .catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    const t = setTimeout(() => load(1, search), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [search, statusFilter]);
  useEffect(() => { load(page, search); }, [page]);

  const doSettle = async () => {
    try {
      await api.post(`/sales/${settleFor.id}/payments`, { method: settle.method, amount: Number(settle.amount) });
      toast.success("Règlement enregistré");
      setSettleFor(null); setSettle({ method: "Espèces", amount: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doCancel = async () => {
    try {
      await api.post(`/sales/${cancelFor.id}/cancel`);
      toast.success("Vente annulée — stock restauré");
      setCancelFor(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    { key: "number", label: "N°", render: (s) => <span className="font-money font-bold">{s.number}</span> },
    { key: "created_at", label: "Date", render: (s) => fmtDateTime(s.created_at) },
    { key: "customer_name", label: "Client" },
    { key: "total", label: "Total", className: "text-right", render: (s) => <span className="font-money font-bold">{fmtMoney(s.total, cur)}</span> },
    { key: "paid", label: "Payé", className: "text-right", render: (s) => <span className="font-money text-emerald-600">{fmtMoney(s.paid, cur)}</span> },
    { key: "balance", label: "Reste", className: "text-right", render: (s) => <span className={`font-money ${s.balance > 0 ? "text-rose-600 font-bold" : ""}`}>{fmtMoney(s.balance, cur)}</span> },
    { key: "payment_status", label: "Statut", render: (s) => <Badge status={s.status === "cancelled" ? "cancelled" : s.payment_status} /> },
    { key: "created_by_name", label: "Vendeur" },
    { key: "actions", label: "", className: "text-right", render: (s) => (
      <div className="flex justify-end gap-1">
        <button data-testid={`pdf-sale-${s.id}`} onClick={() => openPdf(`/sales/${s.id}/pdf`)} title="Facture PDF" className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted"><Printer className="h-4 w-4" /></button>
        {s.balance > 0 && s.status === "validated" && <button data-testid={`settle-sale-${s.id}`} onClick={() => { setSettleFor(s); setSettle({ method: "Espèces", amount: String(s.balance) }); }} title="Encaisser" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950"><Coins className="h-4 w-4" /></button>}
        {hasPerm("returns.manage") && s.status === "validated" && <button data-testid={`return-sale-${s.id}`} onClick={() => navigate(`/retours?sale=${s.id}`)} title="Retour client" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"><RotateCcw className="h-4 w-4" /></button>}
        {hasPerm("sales.cancel") && s.status === "validated" && <button data-testid={`cancel-sale-${s.id}`} onClick={() => setCancelFor(s)} title="Annuler" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950"><Ban className="h-4 w-4" /></button>}
      </div>) },
  ];

  return (
    <div className="space-y-4" data-testid="sales-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Ventes</h1>
        <select data-testid="sales-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${selectCls} w-48`}>
          <option value="">Tous les statuts</option>
          <option value="paid">Payées</option>
          <option value="partial">Partielles</option>
          <option value="credit">À crédit</option>
        </select>
      </div>
      <DataTable testid="sales-table" columns={columns} rows={rows} serverSide
        serverTotal={pg.total} serverPage={page} serverPages={pg.pages}
        onPageChange={setPage} onSearch={(v) => { setSearch(v); setPage(1); }} />

      <Modal open={!!settleFor} onClose={() => setSettleFor(null)} title={`Encaisser — ${settleFor?.number}`}>
        <div className="space-y-3">
          <p className="text-sm">Reste à payer : <b className="font-money">{fmtMoney(settleFor?.balance, cur)}</b></p>
          <Field label="Mode de paiement">
            <select data-testid="settle-method" value={settle.method} onChange={(e) => setSettle({ ...settle, method: e.target.value })} className={selectCls}>
              {PAYMENT_METHODS.map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Montant"><input data-testid="settle-amount" type="number" min="0" value={settle.amount} onChange={(e) => setSettle({ ...settle, amount: e.target.value })} className={inputCls} /></Field>
          <button data-testid="settle-confirm" onClick={doSettle} disabled={!Number(settle.amount)} className={`${btnPrimary} w-full`}>Enregistrer le règlement</button>
        </div>
      </Modal>

      <Modal open={!!cancelFor} onClose={() => setCancelFor(null)} title="Annuler la vente">
        <p className="text-sm">Annuler <b>{cancelFor?.number}</b> ({fmtMoney(cancelFor?.total, cur)}) ? Le stock sera restauré et l'opération tracée dans le journal d'audit.</p>
        <div className="flex gap-2 mt-4">
          <button data-testid="confirm-cancel-sale" onClick={doCancel} className={`${btnDanger} flex-1`}>Confirmer l'annulation</button>
          <button onClick={() => setCancelFor(null)} className={btnSecondary}>Retour</button>
        </div>
      </Modal>
    </div>
  );
}
